import FleetPageState from '../../components/FleetPageState';
import PageHeader from '../../components/PageHeader';
// src/pages/AnalyticsPage.tsx - FIXED WITH THEME-AWARE TIME PERIOD SELECTOR
import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
  Button,
  Alert,
  Tabs,
  Tab,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import { Refresh as RefreshIcon, Download as DownloadIcon } from '@mui/icons-material';
import { useQuery } from '@tanstack/react-query';
import { analyticsAPI } from '../../services/api';
import { useThemeContext } from '../../contexts/ThemeContext';  // ✅ ADDED
import RealFleetMileageChart from '../../components/RealFleetMileageChart';
import RealFuelTrendChart from '../../components/RealFuelTrendChart';
import RealEngineHealthWidget from '../../components/RealEngineHealthWidget';
import RealIdleTimeWidget from '../../components/RealIdleTimeWidget';
import RealBatteryHealthWidget from '../../components/RealBatteryHealthWidget';
import RealTripAnalysisWidget from '../../components/RealTripAnalysisWidget';

const AnalyticsPage: React.FC = () => {
  const [period, setPeriod] = useState('7d');
  const [activeTab, setActiveTab] = useState(0);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const { mode } = useThemeContext();  // ✅ ADDED

  const {
    data: analyticsData,
    isLoading,
    isError,
    error,
    refetch,
    isFetching
  } = useQuery({
    queryKey: ['analytics-initial-load', period],
    queryFn: ({ signal }) => analyticsAPI.getAnalyticsInitialLoad(period, signal),
    retry: false,
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
    refetchOnWindowFocus: false,
  });

  const handleExportReport = () => {
    if (!analyticsData?.report) return;

    const dataStr = JSON.stringify(analyticsData.report, null, 2);
    const dataUri = 'data:application/json;charset=utf-8,' + encodeURIComponent(dataStr);
    const exportFileDefaultName = `fleet-analytics-${new Date().toISOString().split('T')[0]}.json`;

    const linkElement = document.createElement('a');
    linkElement.setAttribute('href', dataUri);
    linkElement.setAttribute('download', exportFileDefaultName);
    linkElement.click();
  };

  if (isLoading || (isError && !analyticsData)) {
    return <FleetPageState page="analytics" error={isError ? error : undefined} onRetry={() => refetch()} />;
  }

  const { report, fuel, mileage, engine, idle, battery, trip, theft } = analyticsData || {};

  return (
    <Box>
      {isError && <Alert severity="warning" sx={{ mb: 2 }} action={<Button onClick={() => refetch()}>Retry</Button>}>Couldn’t refresh analytics. Showing the last loaded data.</Alert>}
      {/* Header - RESPONSIVE */}
      <PageHeader title="Fleet Analytics & Reports" description="Explore mileage, fuel usage, engine health, and fleet performance" icon="analytics" actions={<>
{isFetching && <CircularProgress size={20} sx={{ color: 'white' }} />}
<Box sx={{
          display: 'flex',
          flexDirection: { xs: 'column', sm: 'row' },
          gap: 2,
          width: { xs: '100%', sm: 'auto' }
        }}>
          {/* ✅ FIXED: Theme-aware Time Period Selector */}
          <FormControl size="small" sx={{ minWidth: { xs: '100%', sm: 150 } }}>
            <InputLabel
              sx={{
                color: mode === 'dark' ? 'rgba(255,255,255,0.7)' : 'rgba(0,0,0,0.6)',
              }}
            >
              Time Period
            </InputLabel>
            <Select
              value={period}
              label="Time Period"
              onChange={(e) => setPeriod(e.target.value)}
              sx={{
                bgcolor: mode === 'dark' ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.02)',
                color: mode === 'dark' ? '#ffffff' : '#000000',
                '& .MuiOutlinedInput-notchedOutline': {
                  borderColor: mode === 'dark' ? 'rgba(255,255,255,0.2)' : 'rgba(0,0,0,0.2)',
                },
                '&:hover .MuiOutlinedInput-notchedOutline': {
                  borderColor: mode === 'dark' ? 'rgba(255,255,255,0.3)' : 'rgba(0,0,0,0.3)',
                },
                '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                  borderColor: 'primary.main',
                },
                '& .MuiSelect-icon': {
                  color: mode === 'dark' ? 'rgba(255,255,255,0.7)' : 'rgba(0,0,0,0.6)',
                },
              }}
              MenuProps={{
                PaperProps: {
                  sx: {
                    bgcolor: mode === 'dark' ? '#2a2a2a' : '#ffffff',
                    '& .MuiMenuItem-root': {
                      color: mode === 'dark' ? '#ffffff' : '#000000',
                      '&:hover': {
                        bgcolor: mode === 'dark' ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.05)',
                      },
                      '&.Mui-selected': {
                        bgcolor: mode === 'dark' ? 'rgba(25,118,210,0.2)' : 'rgba(25,118,210,0.1)',
                        '&:hover': {
                          bgcolor: mode === 'dark' ? 'rgba(25,118,210,0.3)' : 'rgba(25,118,210,0.15)',
                        },
                      },
                    },
                  },
                },
              }}
            >
              <MenuItem value="7d">Last 7 Days</MenuItem>
              <MenuItem value="30d">Last 30 Days</MenuItem>
              <MenuItem value="90d">Last 90 Days</MenuItem>
            </Select>
          </FormControl>

          <Box sx={{ display: 'flex', gap: 1, width: { xs: '100%', sm: 'auto' } }}>
            <Button
              variant="outlined"
              startIcon={!isMobile && <RefreshIcon />}
              onClick={() => refetch()}
              disabled={isFetching}
              fullWidth={isMobile}
              size={isMobile ? 'small' : 'medium'}
            >
              {isMobile ? 'Refresh' : 'Refresh'}
            </Button>

            <Button
              variant="contained"
              startIcon={!isMobile && <DownloadIcon />}
              onClick={handleExportReport}
              fullWidth={isMobile}
              size={isMobile ? 'small' : 'medium'}
            >
              {isMobile ? 'Export' : 'Export Report'}
            </Button>
          </Box>
        </Box>
</>} />

      {/* Fuel Theft Alerts */}
      {theft && theft.summary.totalEvents > 0 && (
        <Alert severity="warning" sx={{ mb: 3 }}>
          <Typography variant="body2" sx={{ fontWeight: 600, fontSize: { xs: 12, sm: 14 } }}>
            {theft.summary.totalEvents} Suspicious Fuel Events Detected (Last 24h)
          </Typography>
          <Typography variant="caption" sx={{ fontSize: { xs: 10, sm: 12 } }}>
            {theft.summary.highSuspicion} high priority • Total estimated loss: {theft.summary.totalFuelLoss.toFixed(1)}%
          </Typography>
        </Alert>
      )}

      {/* Summary Stats Cards - RESPONSIVE */}
      {report && (
        <Grid container spacing={{ xs: 1.5, sm: 2 }} sx={{ mb: 3 }}>
          <Grid item xs={6} sm={6} md={3}>
            <Card>
              <CardContent sx={{ p: { xs: 1.5, sm: 2 } }}>
                <Typography color="textSecondary" variant="body2" sx={{ fontSize: { xs: 11, sm: 14 } }}>
                  Fleet Health
                </Typography>
                <Typography
                  variant={isMobile ? 'h5' : 'h4'}
                  color={
                    report.health.avgHealthScore >= 80 ? 'success.main' :
                      report.health.avgHealthScore >= 60 ? 'warning.main' : 'error.main'
                  }
                >
                  {report.health.avgHealthScore}
                </Typography>
                <Typography variant="caption" color="textSecondary" sx={{ fontSize: { xs: 9, sm: 12 } }}>
                  {report.health.healthy} healthy, {report.health.critical} critical
                </Typography>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={6} sm={6} md={3}>
            <Card>
              <CardContent sx={{ p: { xs: 1.5, sm: 2 } }}>
                <Typography color="textSecondary" variant="body2" sx={{ fontSize: { xs: 11, sm: 14 } }}>
                  Fuel Consumed
                </Typography>
                <Typography variant={isMobile ? 'h5' : 'h4'} color="primary.main">
                  {(report.fuel.totalConsumedLiters || 0).toFixed(1)} L
                </Typography>
                <Typography variant="caption" color="textSecondary" sx={{ fontSize: { xs: 9, sm: 12 } }}>
                  Avg {(report.fuel.avgDailyConsumptionLiters || 0).toFixed(1)}L per day
                </Typography>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={6} sm={6} md={3}>
            <Card>
              <CardContent sx={{ p: { xs: 1.5, sm: 2 } }}>
                <Typography color="textSecondary" variant="body2" sx={{ fontSize: { xs: 11, sm: 14 } }}>
                  Total Distance
                </Typography>
                <Typography variant={isMobile ? 'h5' : 'h4'} color="info.main">
                  {report.efficiency.totalTripDistance || 0} km
                </Typography>
                <Typography variant="caption" color="textSecondary" sx={{ fontSize: { xs: 9, sm: 12 } }}>
                  {report.efficiency.totalTrips} trips completed
                </Typography>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={6} sm={6} md={3}>
            <Card>
              <CardContent sx={{ p: { xs: 1.5, sm: 2 } }}>
                <Typography color="textSecondary" variant="body2" sx={{ fontSize: { xs: 11, sm: 14 } }}>
                  Idle Time
                </Typography>
                <Typography variant={isMobile ? 'h5' : 'h4'} color="warning.main">
                  {Math.round(report.efficiency.totalIdleTime / 60)} hrs
                </Typography>
                <Typography variant="caption" color="textSecondary" sx={{ fontSize: { xs: 9, sm: 12 } }}>
                  Avg {report.efficiency.avgIdlePerVehicle} min/vehicle
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}

      {/* Tabs - RESPONSIVE */}
      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
        <Tabs
          value={activeTab}
          onChange={(e, v) => setActiveTab(v)}
          variant={isMobile ? 'scrollable' : 'standard'}
          scrollButtons={isMobile ? 'auto' : false}
          sx={{
            '& .MuiTab-root': {
              fontSize: { xs: 12, sm: 14 },
              minWidth: { xs: 80, sm: 120 }
            }
          }}
        >
          <Tab label="Overview" />
          <Tab label="Fuel & Efficiency" />
          <Tab label="Engine Health" />
          <Tab label="Trips & Usage" />
        </Tabs>
      </Box>

      {/* Tab Content - FIXED HEIGHTS */}
      {activeTab === 0 && (
        <Grid container spacing={{ xs: 2, sm: 3 }}>
          <Grid item xs={12} md={6}>
            <Card sx={{ height: { xs: 350, md: 400 } }}>
              <CardContent sx={{
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                p: { xs: 1.5, sm: 2 },
                '&:last-child': { pb: { xs: 1.5, sm: 2 } }
              }}>
                <Typography variant={isMobile ? 'subtitle1' : 'h6'} gutterBottom sx={{ fontWeight: 600, mb: 1 }}>
                  Fleet Mileage Trend
                </Typography>
                <Box sx={{ flexGrow: 1, minHeight: 0, width: '100%' }}>
                  <RealFleetMileageChart data={mileage} />
                </Box>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} md={6}>
            <Card sx={{ height: { xs: 350, md: 400 } }}>
              <CardContent sx={{
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                p: { xs: 1.5, sm: 2 },
                '&:last-child': { pb: { xs: 1.5, sm: 2 } }
              }}>
                <Typography variant={isMobile ? 'subtitle1' : 'h6'} gutterBottom sx={{ fontWeight: 600, mb: 1 }}>
                  Engine Health Status
                </Typography>
                <Box sx={{ flexGrow: 1, minHeight: 0 }}>
                  <RealEngineHealthWidget data={engine?.data || []} />
                </Box>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} md={6}>
            <Card sx={{ height: { xs: 350, md: 400 } }}>
              <CardContent sx={{
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                p: { xs: 1.5, sm: 2 },
                '&:last-child': { pb: { xs: 1.5, sm: 2 } }
              }}>
                <Typography variant={isMobile ? 'subtitle1' : 'h6'} gutterBottom sx={{ fontWeight: 600, mb: 1 }}>
                  Idle Time Analysis
                </Typography>
                <Box sx={{ flexGrow: 1, minHeight: 0 }}>
                  <RealIdleTimeWidget data={idle?.data || []} />
                </Box>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} md={6}>
            <Card sx={{ height: { xs: 350, md: 400 } }}>
              <CardContent sx={{
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                p: { xs: 1.5, sm: 2 },
                '&:last-child': { pb: { xs: 1.5, sm: 2 } }
              }}>
                <Typography variant={isMobile ? 'subtitle1' : 'h6'} gutterBottom sx={{ fontWeight: 600, mb: 1 }}>
                  Battery Health Overview
                </Typography>
                <Box sx={{ flexGrow: 1, minHeight: 0 }}>
                  <RealBatteryHealthWidget data={battery?.data || []} />
                </Box>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}

      {activeTab === 1 && (
        <Grid container spacing={{ xs: 2, sm: 3 }}>
          <Grid item xs={12}>
            <Card sx={{ height: { xs: 350, md: 400 } }}>
              <CardContent sx={{
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                p: { xs: 1.5, sm: 2 },
                '&:last-child': { pb: { xs: 1.5, sm: 2 } }
              }}>
                <Typography variant={isMobile ? 'subtitle1' : 'h6'} gutterBottom sx={{ fontWeight: 600, mb: 1 }}>
                  Fuel Consumption Trend
                </Typography>
                <Box sx={{ flexGrow: 1, minHeight: 0, width: '100%' }}>
                  <RealFuelTrendChart data={fuel || []} />
                </Box>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} md={6}>
            <Card sx={{ height: { xs: 350, md: 400 } }}>
              <CardContent sx={{
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                p: { xs: 1.5, sm: 2 },
                '&:last-child': { pb: { xs: 1.5, sm: 2 } }
              }}>
                <Typography variant={isMobile ? 'subtitle1' : 'h6'} gutterBottom sx={{ fontWeight: 600, mb: 1 }}>
                  Idle Time Breakdown
                </Typography>
                <Box sx={{ flexGrow: 1, minHeight: 0 }}>
                  <RealIdleTimeWidget data={idle?.data || []} />
                </Box>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} md={6}>
            <Card sx={{ height: { xs: 'auto', md: 400 }, minHeight: { xs: 250 } }}>
              <CardContent sx={{ p: { xs: 1.5, sm: 2 } }}>
                <Typography variant={isMobile ? 'subtitle1' : 'h6'} gutterBottom sx={{ fontWeight: 600 }}>
                  Fuel Efficiency Metrics
                </Typography>
                {report && (
                  <Box sx={{ p: { xs: 1, sm: 2 } }}>
                    <Typography variant={isMobile ? 'h4' : 'h3'} sx={{ mb: 2 }}>
                      {report.fuel.avgDailyConsumptionLiters?.toFixed(1) || 0} L
                    </Typography>
                    <Typography variant="body2" color="textSecondary" gutterBottom sx={{ fontSize: { xs: 12, sm: 14 } }}>
                      Average Daily Consumption
                    </Typography>
                    <Box sx={{ mt: 3 }}>
                      <Typography variant="body2" sx={{ mb: 1, fontSize: { xs: 12, sm: 14 } }}>
                        Total Consumed: {report.fuel.totalConsumedLiters?.toFixed(1) || 0} L
                      </Typography>
                      <Typography variant="body2" sx={{ mb: 1, fontSize: { xs: 12, sm: 14 } }}>
                        Total Refueled: {report.fuel.totalRefueledLiters?.toFixed(1) || 0} L
                      </Typography>
                      <Typography variant="body2" sx={{ fontSize: { xs: 12, sm: 14 } }}>
                        Period: {report.period}
                      </Typography>
                    </Box>
                  </Box>
                )}
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}

      {activeTab === 2 && (
        <Grid container spacing={{ xs: 2, sm: 3 }}>
          <Grid item xs={12} md={8}>
            <Card sx={{ height: { xs: 400, md: 500 } }}>
              <CardContent sx={{
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                p: { xs: 1.5, sm: 2 }
              }}>
                <Typography variant={isMobile ? 'subtitle1' : 'h6'} gutterBottom sx={{ fontWeight: 600 }}>
                  Engine Health Details
                </Typography>
                <Box sx={{ flexGrow: 1, minHeight: 0 }}>
                  <RealEngineHealthWidget data={engine?.data || []} />
                </Box>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} md={4}>
            <Card sx={{ height: { xs: 'auto', md: 500 }, minHeight: { xs: 300 } }}>
              <CardContent sx={{ p: { xs: 1.5, sm: 2 } }}>
                <Typography variant={isMobile ? 'subtitle1' : 'h6'} gutterBottom sx={{ fontWeight: 600 }}>
                  Top Issues
                </Typography>
                {report && report.health.topIssues && (
                  <Box sx={{ mt: 2 }}>
                    {Object.entries(report.health.topIssues)
                      .sort(([, a]: any, [, b]: any) => b - a)
                      .slice(0, 5)
                      .map(([issue, count]: any) => (
                        <Box key={issue} sx={{ mb: 2, p: 1, bgcolor: 'action.hover', borderRadius: 1 }}>
                          <Typography variant="body2" sx={{ fontWeight: 500, fontSize: { xs: 12, sm: 14 } }}>
                            {issue.replace(/_/g, ' ').toUpperCase()}
                          </Typography>
                          <Typography variant="caption" color="textSecondary" sx={{ fontSize: { xs: 10, sm: 12 } }}>
                            {count} vehicle{count > 1 ? 's' : ''} affected
                          </Typography>
                        </Box>
                      ))}
                  </Box>
                )}
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}

      {activeTab === 3 && (
        <Grid container spacing={{ xs: 2, sm: 3 }}>
          <Grid item xs={12} md={6}>
            <Card sx={{ height: { xs: 350, md: 400 } }}>
              <CardContent sx={{
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                p: { xs: 1.5, sm: 2 }
              }}>
                <Typography variant={isMobile ? 'subtitle1' : 'h6'} gutterBottom sx={{ fontWeight: 600 }}>
                  Trip Statistics
                </Typography>
                <Box sx={{ flexGrow: 1, minHeight: 0 }}>
                  <RealTripAnalysisWidget data={trip?.data || []} />
                </Box>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} md={6}>
            <Card sx={{ height: { xs: 350, md: 400 } }}>
              <CardContent sx={{
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                p: { xs: 1.5, sm: 2 }
              }}>
                <Typography variant={isMobile ? 'subtitle1' : 'h6'} gutterBottom sx={{ fontWeight: 600 }}>
                  Mileage Distribution
                </Typography>
                <Box sx={{ flexGrow: 1, minHeight: 0 }}>
                  <RealFleetMileageChart data={mileage} />
                </Box>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12}>
            <Card>
              <CardContent sx={{ p: { xs: 1.5, sm: 2 } }}>
                <Typography variant={isMobile ? 'subtitle1' : 'h6'} gutterBottom sx={{ fontWeight: 600 }}>
                  Efficiency Summary
                </Typography>
                {report && (
                  <Grid container spacing={{ xs: 1.5, sm: 2 }} sx={{ mt: 1 }}>
                    <Grid item xs={6} sm={3}>
                      <Typography variant="body2" color="textSecondary" sx={{ fontSize: { xs: 11, sm: 14 } }}>
                        Total Trips
                      </Typography>
                      <Typography variant={isMobile ? 'h6' : 'h5'}>{report.efficiency.totalTrips}</Typography>
                    </Grid>
                    <Grid item xs={6} sm={3}>
                      <Typography variant="body2" color="textSecondary" sx={{ fontSize: { xs: 11, sm: 14 } }}>
                        Total Distance
                      </Typography>
                      <Typography variant={isMobile ? 'h6' : 'h5'}>
                        {report.efficiency.totalTripDistance.toFixed(1)} km
                      </Typography>
                    </Grid>
                    <Grid item xs={6} sm={3}>
                      <Typography variant="body2" color="textSecondary" sx={{ fontSize: { xs: 11, sm: 14 } }}>
                        Avg Trip Distance
                      </Typography>
                      <Typography variant={isMobile ? 'h6' : 'h5'}>{report.efficiency.avgTripDistance} km</Typography>
                    </Grid>
                    <Grid item xs={6} sm={3}>
                      <Typography variant="body2" color="textSecondary" sx={{ fontSize: { xs: 11, sm: 14 } }}>
                        Total Idle Time
                      </Typography>
                      <Typography variant={isMobile ? 'h6' : 'h5'}>
                        {Math.round(report.efficiency.totalIdleTime / 60)} hrs
                      </Typography>
                    </Grid>
                  </Grid>
                )}
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}
    </Box>
  );
};

export default AnalyticsPage;